import type { ComponentType, ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { InfoField } from '@/components/InfoField';
import { cn } from '@/lib/utils';

const topContentTransition = {
    type: 'spring',
    stiffness: 350,
    damping: 35,
    mass: 0.8,
} as const;

interface GenericInfoPanelItem {
    id: string;
    label: string;
    value: string | number | ReactNode;
    icon?: ComponentType<{ className?: string }>;
    link?: string;
}

interface GenericInfoPanelProps {
    title: string;
    items: GenericInfoPanelItem[];
    headerContent?: ReactNode;
    topContent?: ReactNode;
    footerContent?: ReactNode;
    columns?: 1 | 2 | 3 | 4;
    className?: string;
}

const GRID_COLUMNS_CLASS: Record<1 | 2 | 3 | 4, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-2 @medium:grid-cols-3',
    4: 'grid-cols-2 @medium:grid-cols-4',
};

export function GenericInfoPanel({
    title,
    items,
    headerContent,
    topContent,
    footerContent,
    columns = 4,
    className,
}: GenericInfoPanelProps) {
    const reducedMotion = useReducedMotion();

    return (
        <div className={cn('rounded-[10px] bg-grey-600', className)}>
            <div className="mb-6 flex items-center justify-between">
                <h5 className="text-md-medium text-white-100">{title}</h5>
                {headerContent}
            </div>
            <AnimatePresence initial={false}>
                {topContent && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={
                            reducedMotion
                                ? { duration: 0 }
                                : topContentTransition
                        }
                        className="overflow-hidden"
                    >
                        <div className="mb-6">{topContent}</div>
                    </motion.div>
                )}
            </AnimatePresence>
            <div
                className={cn(
                    'grid gap-x-4 gap-y-7',
                    GRID_COLUMNS_CLASS[columns],
                )}
            >
                {items.map((item) => (
                    <InfoField
                        key={item.id}
                        label={item.label}
                        value={item.value}
                        icon={item.icon}
                        link={item.link}
                    />
                ))}
            </div>
            {footerContent}
        </div>
    );
}
